'use client';
import React, { useState } from 'react';
import { TEACHERS, ATTENDANCE_RECORDS } from '@/lib/mockData';
import { CheckCircle2, Clock, AlertCircle, Search } from 'lucide-react';
import AttendanceEntryForm from './AttendanceEntryForm';

export default function TeacherEntryPanel() {
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>('tchr-001');
  const [searchTeacher, setSearchTeacher] = useState('');

  const TODAY = '2026-05-14';

  const todayRecords = ATTENDANCE_RECORDS?.filter((r) => r?.date === TODAY);
  const loggedTeacherIds = new Set(todayRecords.map((r) => r.teacherId));

  const filteredTeachers = TEACHERS?.filter((t) =>
    t?.name?.toLowerCase()?.includes(searchTeacher?.toLowerCase()) ||
    t?.department?.toLowerCase()?.includes(searchTeacher?.toLowerCase())
  );

  const selectedTeacher = TEACHERS?.find((t) => t?.id === selectedTeacherId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
      {/* Left: Teacher List */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="text-sm font-600 text-foreground mb-2" style={{ fontWeight: 600 }}>
            Teachers — 14 May 2026
          </h2>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search teacher…"
              value={searchTeacher}
              onChange={(e) => setSearchTeacher(e?.target?.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
        <div className="divide-y divide-border overflow-y-auto max-h-[calc(100vh-260px)]">
          {filteredTeachers?.map((teacher) => {
            const isLogged = loggedTeacherIds?.has(teacher?.id);
            const record = todayRecords?.find((r) => r?.teacherId === teacher?.id);
            const isSelected = selectedTeacherId === teacher?.id;

            return (
              <button
                key={teacher?.id}
                type="button"
                onClick={() => setSelectedTeacherId(teacher?.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-100 ${
                  isSelected ? 'bg-primary/8 border-l-2 border-primary' : 'hover:bg-muted/50'
                }`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-700 ${
                  isLogged ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`} style={{ fontWeight: 700 }}>
                  {teacher?.name?.split(' ')?.map((n) => n?.[0])?.join('')?.slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-600 truncate ${isSelected ? 'text-primary' : 'text-foreground'}`} style={{ fontWeight: 600 }}>
                    {teacher?.name}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">{teacher?.department}</p>
                </div>
                <div className="shrink-0">
                  {isLogged ? (
                    <span className="flex items-center gap-1 text-[10px] text-green-600 font-500" style={{ fontWeight: 500 }}>
                      <CheckCircle2 size={12} />
                      {record?.status}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[10px] text-amber-600 font-500" style={{ fontWeight: 500 }}>
                      <Clock size={12} />
                      Pending
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {/* Summary Footer */}
        <div className="px-4 py-3 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-1 text-green-600">
              <CheckCircle2 size={12} />
              <span>{loggedTeacherIds?.size} logged</span>
            </div>
            <div className="flex items-center gap-1 text-amber-600">
              <AlertCircle size={12} />
              <span>{TEACHERS?.length - loggedTeacherIds?.size} pending</span>
            </div>
          </div>
        </div>
      </div>
      {/* Right: Entry Form */}
      <div className="lg:col-span-2">
        {selectedTeacher ? (
          <AttendanceEntryForm
            teacher={selectedTeacher}
            existingRecord={ATTENDANCE_RECORDS?.find(
              (r) => r?.teacherId === selectedTeacher?.id && r?.date === TODAY
            )}
          />
        ) : (
          <div className="bg-card border border-border rounded-xl flex items-center justify-center h-64">
            <p className="text-sm text-muted-foreground">Select a teacher from the list to log attendance.</p>
          </div>
        )}
      </div>
    </div>
  );
}